import React, { useState } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { CreateRequeteDialog } from './CreateRequeteDialog';
import { useLanguage } from '../contexts/LanguageContext';
import { SearchIcon, FilterIcon, MapPinIcon, MapIcon, TableIcon, FolderIcon, ChevronLeftIcon, ChevronRightIcon, ChevronsUpDownIcon, PlusIcon, SettingsIcon, XIcon, PaperclipIcon, CheckSquareIcon, SquareIcon, LinkIcon, TagIcon, FileTextIcon, PrinterIcon, SendIcon, LockIcon, BellIcon, CheckIcon } from 'lucide-react';
type ViewMode = 'list' | 'map';
/** Citizen-facing tabs on the left pane. */
type LeftTab = 'infos' | 'messages';
/** Internal tabs on the right pane. */
type RightTab = 'gestion' | 'collaboration' | 'historique';
type Status = 'Nouvelle demande' | 'En Cours' | 'Complété' | 'En Attente' | 'Reçue';
interface Request {
  id: string;
  subject: string;
  category: string;
  reqId: string;
  location: string;
  date: string;
  status: Status;
  department: string;
  requester: {
    name: string;
    initials: string;
    color: string;
  };
  slaDate: string;
}
const MOCK_REQUESTS: Request[] = [{
  id: '1',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-101',
  location: '1520, 1 ère Rue, Richelieu,...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '2',
  subject: 'Bâtiment',
  category: 'Bâtiment',
  reqId: 'R-100',
  location: '205, Avenue 8e, Richelie...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juillet 2026'
}, {
  id: '3',
  subject: 'Nid-de-poule - trait...',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-99',
  location: '2907, Rang de la Savane, ...',
  date: '8 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Caroline Nadeau',
    initials: 'CN',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '4',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-98',
  location: '32, Place Rouville-Tessier,...',
  date: '8 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '13 juin 2026'
}, {
  id: '5',
  subject: 'Divers',
  category: 'Voirie et infrastructures routi...',
  reqId: 'R-97',
  location: '700, Rue Martel, Richelie...',
  date: '4 juin 2026',
  status: 'En Cours',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '9 juin 2026'
}, {
  id: '6',
  subject: 'Plateaux sportifs - t...',
  category: 'Parcs et espaces récréatifs',
  reqId: 'R-96',
  location: '900, Rue des Oblats, Ric...',
  date: '3 juin 2026',
  status: 'Complété',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Nathalie Ouellet',
    initials: 'NO',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '8 juin 2026'
}, {
  id: '7',
  subject: 'Refoulement égout...',
  category: 'Eau potable et égout',
  reqId: 'R-95',
  location: '192, Rue des Érables, Ric...',
  date: '3 juin 2026',
  status: 'Nouvelle demande',
  department: 'Service Des Travaux Publics',
  requester: {
    name: 'Éric Robin',
    initials: 'ÉR',
    color: 'bg-[#4F46E5]'
  },
  slaDate: '4 juin 2026'
}];
/** English equivalents for the French mock data and status labels. */
const EN_DICT: Record<string, string> = {
  // Statuses
  'Nouvelle demande': 'New request',
  'En Cours': 'In progress',
  'Complété': 'Completed',
  'En Attente': 'On hold',
  'Reçue': 'Received',
  // Subjects & categories
  Divers: 'Miscellaneous',
  Bâtiment: 'Building',
  'Nid-de-poule - trait...': 'Pothole - treat...',
  'Plateaux sportifs - t...': 'Sports facilities - t...',
  'Refoulement égout...': 'Sewer backup...',
  'Voirie et infrastructures routi...': 'Roads and road infrastruc...',
  'Parcs et espaces récréatifs': 'Parks and recreational spaces',
  'Eau potable et égout': 'Drinking water and sewer',
  Taxation: 'Taxation',
  Administration: 'Administration',
  'Lampadaire défectueux': 'Faulty streetlight',
  'Éclairage public': 'Street lighting',
  'Eau potable (qualité)': 'Drinking water (quality)',
  'Feux de circulation': 'Traffic lights',
  // Departments
  'Service Des Travaux Publics': 'Public Works Department',
  // Dates
  '8 juin 2026': 'June 8, 2026',
  '4 juin 2026': 'June 4, 2026',
  '3 juin 2026': 'June 3, 2026',
  '13 juin 2026': 'June 13, 2026',
  '9 juin 2026': 'June 9, 2026',
  '4 juin 2026 ': 'June 4, 2026',
  '8 juillet 2026': 'July 8, 2026'
};
const getStatusColor = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-200 text-slate-700';
    case 'En Cours':
      return 'bg-indigo-100 text-indigo-700';
    case 'Complété':
      return 'bg-indigo-100 text-indigo-700';
    case 'En Attente':
      return 'bg-orange-100 text-orange-700';
    case 'Reçue':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};
const getStatusDot = (status: Status) => {
  switch (status) {
    case 'Nouvelle demande':
      return 'bg-slate-400';
    case 'En Cours':
      return 'bg-[#4F46E5]';
    case 'Complété':
      return 'bg-[#4F46E5]';
    case 'En Attente':
      return 'bg-orange-500';
    case 'Reçue':
      return 'bg-slate-400';
    default:
      return 'bg-slate-400';
  }
};
// Request locations around Windsor, QC
const MAP_MARKERS: {
  reqId: string;
  subject: string;
  category: string;
  position: [number, number];
}[] = [{
  reqId: 'R-127',
  subject: 'Taxation',
  category: 'Administration',
  position: [45.4528, -73.4622]
}, {
  reqId: 'R-104',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4468, -73.4698]
}, {
  reqId: 'R-70',
  subject: 'Eau potable (qualité)',
  category: 'Eau potable et égout',
  position: [45.4552, -73.4578]
}, {
  reqId: 'R-103',
  subject: 'Feux de circulation',
  category: 'Éclairage public',
  position: [45.4438, -73.4728]
}, {
  reqId: 'R-57',
  subject: 'Lampadaire défectueux',
  category: 'Éclairage public',
  position: [45.4488, -73.4548]
}];
// Custom indigo map marker
const requestMarkerIcon = L.divIcon({
  className: '',
  html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:#4F46E5;box-shadow:0 2px 6px rgba(0,0,0,0.3);border:2px solid white;"><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"/></svg></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  popupAnchor: [0, -14]
});
export function RequetesGerer() {
  const {
    t,
    lang
  } = useLanguage();
  /** Translates a French mock value to English when the interface is in English. */
  const tr = (s: string) => lang === 'en' ? EN_DICT[s] ?? s : s;
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [leftTab, setLeftTab] = useState<LeftTab>('infos');
  const [rightTab, setRightTab] = useState<RightTab>('gestion');
  const [draft, setDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  if (selectedRequest) {
    return <div className="bg-slate-50 -mx-4 sm:-mx-6 lg:-mx-8 -my-8 min-h-screen flex" data-id="element-5272" data-mp="gfzm77z61iej">
        {/* Left Pane - Citizen Visible */}
        <div className="w-1/2 border-r border-slate-200 bg-white flex flex-col" data-id="element-5273" data-mp="145aes26d46hl">
          <div className="p-4 border-b border-slate-200 flex items-center gap-2 text-slate-500 text-sm font-medium" data-id="element-5274" data-mp="194dmjudq8qt9">
            <div className="w-4 h-4 rounded-full border-2 border-slate-300 flex items-center justify-center" data-id="element-5275" data-mp="ve1shpxl0hzh">
              <div className="w-1.5 h-1.5 bg-slate-300 rounded-full" data-id="element-5276" data-mp="zdsezhg2sby7"></div>
            </div>
            {t('Visible par le citoyen', 'Visible to the citizen')}
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6" data-id="element-5277" data-mp="1rvo95zcf6hfc">
            <button onClick={() => setLeftTab('infos')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${leftTab === 'infos' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5278" data-mp="1rwz9tqakj928">
              {t('Infos', 'Details')}
            </button>
            <button onClick={() => setLeftTab('messages')} className={`pb-3 border-b-2 font-semibold text-sm transition-colors flex items-center gap-2 ${leftTab === 'messages' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5279" data-mp="2tmfpbl3w4tf0">
              {t('Messages', 'Messages')}
              <span className="px-1.5 py-0.5 rounded-full bg-[#4F46E5] text-white text-[10px] font-bold" data-id="element-5280" data-mp="itl9rt7abc1n">
                1
              </span>
            </button>
          </div>

          {leftTab === 'messages' && <>
              <div className="p-6 overflow-y-auto flex-1 space-y-5" data-id="element-5281" data-mp="307le4bmy41xx">
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-id="element-5282" data-mp="2v7qmu6z9dacc">
                  <BellIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-5283" data-mp="2qdcnxule0l2x" />
                  {t('Nathalie Ouellet reçoit chaque message par courriel et notification bciti+.', 'Nathalie Ouellet receives every message by email and bciti+ notification.')}
                </div>

                <div className="text-center" data-id="element-5284" data-mp="27g1rehr545r6">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5285" data-mp="27ixoy4z2ssf3">
                    {t('19 mai 2026', 'May 19, 2026')}
                  </span>
                </div>

                {/* Citizen message */}
                <div className="flex gap-3" data-id="element-5286" data-mp="q3wsn7jqwkx">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-5287" data-mp="r6jsk1r0evk">
                    NO
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5288" data-mp="d76bl79j7l97">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed" data-id="element-5289" data-mp="hz66umenppzg">
                      {t("Bonjour, je voulais savoir si quelqu'un est passé au parc. Les branches sont toujours sur le sentier ce matin et les enfants passent par là pour aller à l'école.", 'Hello, I wanted to know if someone came by the park. The branches are still on the path this morning and the children walk through there to get to school.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5290" data-mp="1aon3xxd81v20">
                      Nathalie Ouellet · {t('19 mai, 08:12', 'May 19, 08:12')}
                    </div>
                  </div>
                </div>

                {/* Staff reply */}
                <div className="flex gap-3 flex-row-reverse" data-id="element-5291" data-mp="3borsgyf7e3r0">
                  <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5292" data-mp="36sb6oavh81h3">
                    JD
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5293" data-mp="3gpft8bt8hyyf">
                    <div className="bg-[#4F46E5] rounded-xl rounded-tr-sm p-3.5 text-sm text-white leading-relaxed" data-id="element-5294" data-mp="3gnl9h0tp8cvx">
                      {t('Bonjour Mme Ouellet, merci pour votre signalement. Une équipe des Travaux publics est planifiée pour le ramassage des branches le 21 mai en avant-midi. Les poubelles ont été vidées hier.', 'Hello Ms. Ouellet, thank you for your report. A Public Works crew is scheduled to pick up the branches on the morning of May 21. The garbage bins were emptied yesterday.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 text-right flex items-center justify-end gap-1.5" data-id="element-5295" data-mp="258prb4vuftam">
                      Jacques Dubois · {t('19 mai, 09:47', 'May 19, 09:47')}
                      <CheckIcon className="w-3 h-3 text-emerald-500" data-id="element-5296" data-mp="1zz585oyjpnq9" />
                      <span className="text-emerald-600 font-medium" data-id="element-5297" data-mp="1l9zaw6j9q7l0">{t('Lu', 'Read')}</span>
                    </div>
                  </div>
                </div>

                <div className="text-center" data-id="element-5298" data-mp="2dwts8uksgqu9">
                  <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-5299" data-mp="2dxv77a4xdxh9">
                    {t('20 mai 2026', 'May 20, 2026')}
                  </span>
                </div>

                {/* Automated status message */}
                <div className="flex gap-3" data-id="element-5300" data-mp="28x7g6ogp5gn1">
                  <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5301" data-mp="2903dcakq01af">
                    <BellIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5302" data-mp="xjd22d1fwj9y" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5303" data-mp="1dpq2pa7k60e0">
                    <div className="bg-white border border-dashed border-slate-300 rounded-xl p-3.5 text-sm text-slate-600 leading-relaxed" data-id="element-5304" data-mp="1drauwt6eh78p">
                      <span className="font-semibold text-slate-700" data-id="element-5305" data-mp="13zwzawhkkrku">
                        {t('Mise à jour automatique', 'Automatic update')}
                      </span>
                      <br data-id="element-5306" data-mp="yyzba39vp3bf" />
                      {t('Le statut de votre requête est passé à « En cours ». Échéance prévue : 26 mai 2026.', 'Your request status changed to “In progress”. Expected due date: May 26, 2026.')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5307" data-mp="1hrtp2tvrmpe2">
                      {t('Envoyé automatiquement · 20 mai, 07:00', 'Sent automatically · May 20, 07:00')}
                    </div>
                  </div>
                </div>

                {/* Latest citizen message, unanswered */}
                <div className="flex gap-3" data-id="element-5308" data-mp="2i01fti0klm7d">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0" data-id="element-5309" data-mp="2i1co9v4yni4t">
                    NO
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-5310" data-mp="2rx5y36raoxiw">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl rounded-tl-sm p-3.5 text-sm text-slate-700 leading-relaxed" data-id="element-5311" data-mp="2woe5xycz8aty">
                      {t('Parfait, merci beaucoup pour le suivi rapide !', 'Perfect, thank you very much for the quick follow-up!')}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1" data-id="element-5312" data-mp="3amclh731adb1">
                      Nathalie Ouellet · {t('20 mai, 11:23', 'May 20, 11:23')} ·{' '}
                      <span className="text-[#4F46E5] font-medium" data-id="element-5313" data-mp="1gbfvvxzt3c8h">
                        {t('Sans réponse', 'Unanswered')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Composer */}
              <div className="border-t border-slate-200 p-4 bg-white" data-id="element-5314" data-mp="197ptzo3clzjz">
                <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={3} placeholder={t('Écrire au citoyen...', 'Write to the citizen...')} className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5315" data-mp="199khna6085rr" />
                <div className="flex items-center justify-between mt-2.5" data-id="element-5316" data-mp="25y0abf4euv93">
                  <div className="flex items-center gap-1" data-id="element-5317" data-mp="20ydssthjusuh">
                    <button aria-label={t('Joindre un fichier', 'Attach a file')} className="p-2 rounded-lg text-slate-400 hover:text-[#4F46E5] hover:bg-indigo-50 transition-colors" data-id="element-5318" data-mp="20yxcofqrfrb5">
                      <PaperclipIcon className="w-4 h-4" data-id="element-5319" data-mp="2uimtrs9hoj2a" />
                    </button>
                    <span className="text-xs text-slate-400" data-id="element-5320" data-mp="2yjyizwogb29y">
                      {t('Visible par le citoyen', 'Visible to the citizen')}
                    </span>
                  </div>
                  <button disabled={!draft.trim()} onClick={() => setDraft('')} className="inline-flex items-center gap-2 px-4 py-2 bg-[#4F46E5] text-white text-sm font-semibold rounded-lg hover:bg-[#4338CA] disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-5321" data-mp="2kmjowen966bn">
                    <SendIcon className="w-4 h-4" data-id="element-5322" data-mp="21uzacgjyfyh8" />
                    {t('Envoyer', 'Send')}
                  </button>
                </div>
              </div>
            </>}

          <div className={`p-6 overflow-y-auto flex-1 ${leftTab === 'infos' ? '' : 'hidden'}`} data-id="element-5323" data-mp="1rwjjmeouar89">
            <div className="flex justify-between items-start mb-8" data-id="element-5324" data-mp="1wu39kbuwgsg1">
              <div className="flex gap-4" data-id="element-5325" data-mp="2ccw4gkl78zmw">
                <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center flex-shrink-0" data-id="element-5326" data-mp="2cbtdkcnvmx6z">
                  <FolderIcon className="w-6 h-6 text-[#4F46E5]" data-id="element-5327" data-mp="23bo8i965oavz" />
                </div>
                <div data-id="element-5328" data-mp="1yctq38c3ooce">
                  <div className="flex items-center gap-2" data-id="element-5329" data-mp="1ydmp1n4ti9jz">
                    <h2 className="text-xl font-bold text-slate-900" data-id="element-5330" data-mp="1yf7j7bn7zqhb">
                      {t('Entretien des parcs', 'Park maintenance')}
                    </h2>
                    <ChevronLeftIcon className="w-4 h-4 text-slate-400 rotate-[-90deg]" data-id="element-5331" data-mp="1om8kv21x8lki" />
                  </div>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-2" data-id="element-5332" data-mp="1klomxw9ixdxb">
                    <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-medium border border-slate-200" data-id="element-5333" data-mp="1fnww75km7xup">
                      R-71
                    </span>
                    <span data-id="element-5334" data-mp="15nw7t57w2rjn">{tr('Parcs et espaces récréatifs')}</span>
                  </div>
                  <div className="text-sm text-slate-400 mt-1 ml-10 border-l-2 border-slate-200 pl-2" data-id="element-5335" data-mp="vkqyukj5iyb6">
                    {tr('Service Des Travaux Publics')}
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 ${getStatusColor('En Cours')}`} data-id="element-5336" data-mp="15jge05jny4nq">
                <div className={`w-2 h-2 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5337" data-mp="ctzrfoiq5hzf"></div>
                {tr('En Cours')}
              </div>
            </div>

            <div className="mb-8" data-id="element-5338" data-mp="mrxc621dkwna">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5339" data-mp="mqw2a0w3ig75">
                {t('DESCRIPTION', 'DESCRIPTION')}
              </h3>
              <div className="p-4 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm leading-relaxed" data-id="element-5340" data-mp="3q8nt3byhwu3r">
                {t("Le parc près de la rue Principale nécessite un entretien urgent. Les poubelles débordent et il y a des branches cassées sur le sentier principal suite à la tempête d'hier soir. Cela représente un danger pour les piétons et les cyclistes.", "The park near Principale Street needs urgent maintenance. The garbage bins are overflowing and there are broken branches on the main path following last night's storm. This is a hazard for pedestrians and cyclists.")}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-8 mb-8" data-id="element-5341" data-mp="1djaipxtagnv4">
              <div data-id="element-5342" data-mp="1djsnxjxxicf6">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5343" data-mp="1dh6hxoqos4z2">
                  {t('DEMANDEUR', 'REQUESTER')}
                </h3>
                <div className="flex items-start gap-3" data-id="element-5344" data-mp="1mcmfafqnh29e">
                  <div className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-5345" data-mp="ptoo7nnv2wtg">
                    NO
                  </div>
                  <div data-id="element-5346" data-mp="mbsl25md13bu">
                    <div className="font-semibold text-slate-900 text-sm" data-id="element-5347" data-mp="mc0xxxihg8n1">
                      Nathalie Ouellet
                    </div>
                    <div className="text-sm text-slate-500 mt-0.5" data-id="element-5348" data-mp="hdh65gh7k2mr">
                      (450) 658-1157
                    </div>
                    <div className="text-sm text-slate-500" data-id="element-5349" data-mp="8bzse4r9wd18">
                      adjointe.tp@ville.richelieu.qc.ca
                    </div>
                  </div>
                </div>
              </div>
              <div data-id="element-5350" data-mp="3uruay2dlv1zc">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5351" data-mp="3urc4ye1559e8">
                  {t('EMPLACEMENT', 'LOCATION')}
                </h3>
                <div className="flex items-start gap-2" data-id="element-5352" data-mp="3lqw83newl0ic">
                  <MapPinIcon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-5353" data-mp="3lsh138lkkbe3" />
                  <div data-id="element-5354" data-mp="3gqhxbxlx3cgm">
                    <div className="text-sm text-slate-700" data-id="element-5355" data-mp="3gr1iroas0ize">
                      200 Boulevard Richelieu
                    </div>
                    <div className="text-sm text-slate-700" data-id="element-5356" data-mp="325dups1gcub9">
                      Richelieu, J3L 3R1
                    </div>
                    <button className="text-sm text-[#4F46E5] mt-1 hover:underline" data-id="element-5357" data-mp="3758kfs9hrduq">
                      {t('Voir sur la carte ▾', 'View on map ▾')}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-5358" data-mp="3kvlrztih0qjj">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5359" data-mp="3kv2agdwqg461">
                {t('PIÈCES JOINTES', 'ATTACHMENTS')}
              </h3>
              <div className="flex gap-3" data-id="element-5360" data-mp="3pwj902rqvejg">
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5361" data-mp="3b6kc03bxe95k">
                  <img src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5362" data-mp="3g4di6wd8b35u" />
                </div>
                <div className="w-20 h-20 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5363" data-mp="chvd6vynmwn1">
                  <img src="https://images.unsplash.com/photo-1519331379826-f10be5486c6f?w=200&h=200&fit=crop" alt="Parc" className="w-full h-full object-cover" data-id="element-5364" data-mp="1x4cvq61z0714" />
                </div>
                <button className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5365" data-mp="21wnu34h2bkrk">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5366" data-mp="199jx9ms4icqv" />
                  <span className="text-xs font-medium" data-id="element-5367" data-mp="1n81ed1niwqzt">{t('Téléverser', 'Upload')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane - Internal */}
        <div className="w-1/2 bg-slate-50 flex flex-col relative" data-id="element-5368" data-mp="2ymncrrqxfznv">
          <button onClick={() => setSelectedRequest(null)} aria-label={t('Fermer', 'Close')} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-200 transition-colors z-10" data-id="element-5369" data-mp="2yp1a6792xnyl">
            <XIcon className="w-5 h-5" data-id="element-5370" data-mp="3txv9gb8vf0cr" />
          </button>

          <div className="p-4 border-b border-slate-200 flex items-center justify-between text-slate-500 text-sm font-medium bg-white" data-id="element-5371" data-mp="2qisnxbwgoxd">
            <div className="flex items-center gap-2" data-id="element-5372" data-mp="7rpz9t2b3i2g">
              <div className="w-4 h-4 rounded flex items-center justify-center border-2 border-slate-300" data-id="element-5373" data-mp="cprjwifdrpg1">
                <div className="w-2 h-2 bg-slate-300 rounded-sm" data-id="element-5374" data-mp="gq1obqniy7ss"></div>
              </div>
              {t('Interne — non visible par le citoyen', 'Internal — not visible to the citizen')}
            </div>
            <PrinterIcon className="w-4 h-4 mr-10" data-id="element-5375" data-mp="qgwcj0kbzhgv" />
          </div>

          <div className="px-6 pt-4 border-b border-slate-200 flex gap-6 bg-white" data-id="element-5376" data-mp="vfpt51uwzol9">
            {[{
            id: 'gestion' as const,
            label: t('Gestion', 'Management')
          }, {
            id: 'collaboration' as const,
            label: t('Collaboration', 'Collaboration')
          }, {
            id: 'historique' as const,
            label: t('Historique', 'History')
          }].map(tab => <button key={tab.id} onClick={() => setRightTab(tab.id)} className={`pb-3 border-b-2 font-semibold text-sm transition-colors ${rightTab === tab.id ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-5377" data-mp="2ay5ioj592ay7">
                {tab.label}
              </button>)}
          </div>

          {rightTab === 'collaboration' && <div className="p-6 overflow-y-auto flex-1 space-y-6" data-id="element-5378" data-mp="2oxor7d1z3ob1">
              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5379" data-mp="3medoqujw44em">
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5380" data-mp="3hn42m3xjvj7h">
                  {t('INTERVENANTS ASSIGNÉS', 'ASSIGNED STAFF')}
                </h3>
                <div className="space-y-3" data-id="element-5381" data-mp="1frockwddsbiq">
                  <div className="flex items-center justify-between" data-id="element-5382" data-mp="1koy2se8lgr7m">
                    <div className="flex items-center gap-3" data-id="element-5383" data-mp="15zs3kq9rb0zq">
                      <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5384" data-mp="15zict0gw0psz">
                        JD
                      </div>
                      <div data-id="element-5385" data-mp="wxiwybhlzrrm">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5386" data-mp="x1gggy83mlc1">
                          Jacques Dubois
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5387" data-mp="n1xyuofnvbn5">
                          {t('Travaux publics · Responsable', 'Public Works · Lead')}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-100" data-id="element-5388" data-mp="2hculbonldwlh">
                      {t('Actif', 'Active')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5389" data-mp="5ru21ni6a7b2">
                    <div className="flex items-center gap-3" data-id="element-5390" data-mp="5phbwn29gqmx">
                      <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5391" data-mp="khjfoi7gc3s8">
                        MG
                      </div>
                      <div data-id="element-5392" data-mp="tg4xgb53nery">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5393" data-mp="tfbnl1aug95l">
                          Martin Gauthier
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5394" data-mp="13fvqs9hxbnqg">
                          {t('Parcs et espaces verts · Exécutant', 'Parks and green spaces · Field crew')}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-100" data-id="element-5395" data-mp="358tejqwufq4j">
                      {t('Intervention prévue', 'Intervention scheduled')}
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-5396" data-mp="2jcrr5dnlaj9s">
                    <div className="flex items-center gap-3" data-id="element-5397" data-mp="2o49rq0lpahu8">
                      <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5398" data-mp="2o51qtleiaxp9">
                        ST
                      </div>
                      <div data-id="element-5399" data-mp="1vh5nhxufm7ji">
                        <div className="text-sm font-semibold text-slate-900" data-id="element-5400" data-mp="1vhxe0u969f2x">
                          Sylvie Tremblay
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-5401" data-mp="25cz5vxuu0v97">
                          {t('Réception · Observatrice', 'Front desk · Observer')}
                        </div>
                      </div>
                    </div>
                    <button className="text-xs font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5402" data-mp="3gv08uwua9ukf">
                      {t('Voir', 'View')}
                    </button>
                  </div>
                </div>
                <button className="mt-4 flex items-center gap-2 text-sm font-medium text-[#4F46E5] hover:text-[#4338CA]" data-id="element-5403" data-mp="19903q0wecjqo">
                  <PlusIcon className="w-4 h-4" data-id="element-5404" data-mp="10elp80joxd8m" /> {t('Ajouter un intervenant', 'Add a staff member')}
                </button>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5405" data-mp="vf95hoo48ma9">
                <div className="flex items-center justify-between gap-3 mb-4" data-id="element-5406" data-mp="qhqs9m32fkwg">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-5407" data-mp="lh32t4uhjc4c">
                    {t('NOTES INTERNES', 'INTERNAL NOTES')}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400" data-id="element-5408" data-mp="cgwlt8zkvu0f">
                    <LockIcon className="w-3 h-3" data-id="element-5409" data-mp="2btqv1df4brwg" />
                    {t('Jamais visible par le citoyen', 'Never visible to the citizen')}
                  </span>
                </div>
                <div className="space-y-3" data-id="element-5410" data-mp="2x4v139h3iut0">
                  <div className="flex gap-3" data-id="element-5411" data-mp="2x3tf49yjknwi">
                    <div className="w-7 h-7 rounded-full bg-slate-800 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5412" data-mp="2x2iak7x9vc4c">
                      JD
                    </div>
                    <div className="flex-1" data-id="element-5413" data-mp="31vjs6nov5prm">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5414" data-mp="31wmj2vm6rs7j">
                        {t("Visite sur place effectuée. Trois branches de bonne taille à tronçonner, la nacelle n'est pas nécessaire. Prévoir deux employés pour l'avant-midi.", 'Site visit done. Three large branches to cut up, the bucket truck is not needed. Plan two workers for the morning.')}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5415" data-mp="2eva7f63g7mq">
                        Jacques Dubois · {t('il y a 2 h', '2 h ago')}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-3" data-id="element-5416" data-mp="60gmbypfq1gq">
                    <div className="w-7 h-7 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0" data-id="element-5417" data-mp="a07ikhxqd9sn">
                      MG
                    </div>
                    <div className="flex-1" data-id="element-5418" data-mp="yhiknps92j9">
                      <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-700 border border-slate-200" data-id="element-5419" data-mp="11dfq9tt3n6n">
                        {t("Le camion nacelle est en réparation jusqu'au 22. On peut quand même y aller au sol comme prévu le 21.", 'The bucket truck is under repair until the 22nd. We can still work from the ground as planned on the 21st.')}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1" data-id="element-5420" data-mp="17moblazfl18l">
                        Martin Gauthier · {t('hier, 16:04', 'yesterday, 16:04')}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2" data-id="element-5421" data-mp="1hbbtkwbwnn5m">
                  <input type="text" value={noteDraft} onChange={e => setNoteDraft(e.target.value)} placeholder={t('Ajouter une note interne...', 'Add an internal note...')} className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5422" data-mp="1hbuz1dkdcj2r" />
                  <button disabled={!noteDraft.trim()} onClick={() => setNoteDraft('')} className="px-4 py-2 bg-[#0F172A] text-white text-sm font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-5423" data-mp="tlhh4fvi00lw">
                    {t('Publier', 'Post')}
                  </button>
                </div>
              </div>
            </div>}

          {rightTab === 'historique' && <div className="p-6 overflow-y-auto flex-1" data-id="element-5424" data-mp="2w7wimo39sewl">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5425" data-mp="2waircr1wd0ge">
                {t("JOURNAL D'ACTIVITÉ", 'ACTIVITY LOG')}
              </h3>
              <div className="relative border-l-2 border-slate-200 ml-3 space-y-6" data-id="element-5426" data-mp="357t127z2ghj7">
                <div className="relative pl-6" data-id="element-5427" data-mp="28jv510r8i8ab">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5428" data-mp="28h0mxfvp0zk2"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5429" data-mp="2hj03y86pk3n0">
                    {t('Intervention planifiée au 21 mai', 'Intervention scheduled for May 21')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5430" data-mp="1ov4815oyqfra">
                    Martin Gauthier ({t('Parcs et espaces verts', 'Parks and green spaces')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5431" data-mp="b86xdc2tl8j">
                    {t('20 mai 2026 · 16:04', 'May 20, 2026 · 16:04')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5432" data-mp="3rff0yinw56vd">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#4F46E5] border-2 border-white" data-id="element-5433" data-mp="93s9govho9yn"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5434" data-mp="4c0k2ho2kaa3">
                    {t('Réponse envoyée au citoyen', 'Reply sent to the citizen')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5435" data-mp="e3ept76m66bo">
                    Jacques Dubois ({t('Travaux publics', 'Public Works')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5436" data-mp="o0t16sdok4x4">
                    {t('19 mai 2026 · 09:47', 'May 19, 2026 · 09:47')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5437" data-mp="2tvu5hcu4m23c">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-amber-500 border-2 border-white" data-id="element-5438" data-mp="2psxnnoceua0g"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5439" data-mp="2for2gvux7o8o">
                    {t('Statut changé de « Reçue » à « En cours »', 'Status changed from “Received” to “In progress”')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5440" data-mp="25wbmu573jbqy">
                    Jacques Dubois ({t('Travaux publics', 'Public Works')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5441" data-mp="1vz70o5d93ga5">
                    {t('19 mai 2026 · 09:30', 'May 19, 2026 · 09:30')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5442" data-mp="1n0brqlitns35">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" data-id="element-5443" data-mp="1n1mv4k8cuxtq"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5444" data-mp="3hdl1mefa5flx">
                    {t('Requête assignée aux Travaux publics', 'Request assigned to Public Works')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5445" data-mp="3jy0zgks27xdg">
                    Sylvie Tremblay ({t('Réception', 'Front desk')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5446" data-mp="3fuum6t6j55xh">
                    {t('19 mai 2026 · 08:41', 'May 19, 2026 · 08:41')}
                  </div>
                </div>
                <div className="relative pl-6" data-id="element-5447" data-mp="35zjfmy59z50t">
                  <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-slate-300 border-2 border-white" data-id="element-5448" data-mp="7gm9yvoberrh"></div>
                  <div className="text-sm font-semibold text-slate-900" data-id="element-5449" data-mp="2gzm7sav1t4f">
                    {t('Requête déposée avec deux photos', 'Request submitted with two photos')}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-5450" data-mp="3oxlyev78c5q6">
                    Nathalie Ouellet ({t('Application bciti+', 'bciti+ app')})
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5451" data-mp="q8ww3dur2b2w">
                    {t('19 mai 2026 · 08:12', 'May 19, 2026 · 08:12')}
                  </div>
                </div>
              </div>
            </div>}

          <div className={`p-6 overflow-y-auto flex-1 space-y-6 ${rightTab === 'gestion' ? '' : 'hidden'}`} data-id="element-5452" data-mp="xq35bu8e309i">
            <div className="text-sm text-slate-500 font-medium" data-id="element-5453" data-mp="12owlxviz37dw">
              {t('19 mai', 'May 19')} <span className="mx-2 text-slate-300" data-id="element-5454" data-mp="17ptz1tp4lb8j">→</span>{' '}
              <span className="text-red-500" data-id="element-5455" data-mp="1bqnoiri7uy3j">{t('26 mai', 'May 26')}</span>
            </div>

            {/* Tasks */}
            <div className="space-y-3" data-id="element-5456" data-mp="1gop8dengv1g2">
              <div className="flex items-start gap-3" data-id="element-5457" data-mp="1gnnxba6gaeyc">
                <CheckSquareIcon className="w-5 h-5 text-slate-900 mt-0.5" data-id="element-5458" data-mp="1lfxh08gz2is6" />
                <div className="flex-1" data-id="element-5459" data-mp="1qfac1ghzyw5u">
                  <div className="text-sm text-slate-500 line-through" data-id="element-5460" data-mp="1qdz0guib8k46">
                    {t('Vérifier la disponibilité des équipes', 'Check crew availability')}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5" data-id="element-5461" data-mp="1zh05sszcq76s">
                    {t('Terminée : 22 mai 2026', 'Completed: May 22, 2026')}{' '}
                    <PencilIcon className="w-3 h-3 inline ml-1" data-id="element-5462" data-mp="2ksrod66h0mv5" />
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5463" data-mp="2up31fydvxgqn">
                  <CalendarIcon className="w-3 h-3" data-id="element-5464" data-mp="2un0ddjwalxok" /> {t('22 mai 2026', 'May 22, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5465" data-mp="2po711usqscpz">
                  ML
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5466" data-mp="1wzhu3f509d6b">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5467" data-mp="2bra7hll8oh5e" />
                <div className="flex-1" data-id="element-5468" data-mp="26z0dnuh06wxu">
                  <div className="text-sm text-slate-700" data-id="element-5469" data-mp="26xxp3t75l6l3">
                    {t('Planifier le ramassage des branches', 'Schedule branch pickup')}
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5470" data-mp="1ea1pogq790km">
                  <CalendarIcon className="w-3 h-3" data-id="element-5471" data-mp="3px5bu2hvedh6" /> {t('25 mai 2026', 'May 25, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full bg-[#4F46E5] text-white flex items-center justify-center text-[10px] font-bold" data-id="element-5472" data-mp="3upopezgk58lz">
                  ST
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-5473" data-mp="3l0529hltaslb">
                <SquareIcon className="w-5 h-5 text-slate-300 mt-0.5" data-id="element-5474" data-mp="cjabh0iagynl" />
                <div className="flex-1" data-id="element-5475" data-mp="ck3dxpa7t1zx">
                  <div className="text-sm text-slate-700" data-id="element-5476" data-mp="hiwphc5xyixg">
                    {t('Contacter le demandeur pour suivi', 'Contact the requester for follow-up')}
                  </div>
                </div>
                <div className="text-xs text-red-500 flex items-center gap-1" data-id="element-5477" data-mp="8h6ww30bpdy3">
                  <CalendarIcon className="w-3 h-3" data-id="element-5478" data-mp="2o8ujb4hpno2n" /> {t('20 mai 2026', 'May 20, 2026')}
                </div>
                <div className="w-6 h-6 rounded-full border border-dashed border-slate-300 text-slate-400 flex items-center justify-center text-[10px]" data-id="element-5479" data-mp="2t7nu2p5m50z4">
                  <UserIcon className="w-3 h-3" data-id="element-5480" data-mp="2czw8kcxglsag" />
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-2" data-id="element-5481" data-mp="2htgx7ed5x9wz">
                <PlusIcon className="w-4 h-4" data-id="element-5482" data-mp="230x0owkiq0oi" /> {t('Ajouter une tâche', 'Add a task')}
              </button>
            </div>

            {/* Linked Requests */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5483" data-mp="2vt06iahpmrav">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5484" data-mp="30r1ost7bamla">
                {t('REQUÊTES LIÉES', 'LINKED REQUESTS')}
              </h3>
              <div className="space-y-3" data-id="element-5485" data-mp="2qslw4lsmz59o">
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5486" data-mp="2qrklujjg2mt0">
                  <div className="flex items-center gap-3" data-id="element-5487" data-mp="1fd72nrzmyvjb">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5488" data-mp="1feqixilal6l3">
                      {t('Liée ▾', 'Related ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5489" data-mp="3oo7rft859cn9">
                      R-68 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5490" data-mp="2feyjl9bun2v">
                      {tr('Lampadaire défectueux')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Attente')}`} data-id="element-5491" data-mp="35weyfyt2rrfj">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Attente')}`} data-id="element-5492" data-mp="3ewk4a4imehrl"></div>
                    {tr('En Attente')}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5493" data-mp="2n42y1hlzf081">
                  <div className="flex items-center gap-3" data-id="element-5494" data-mp="v3bb6n1vubt7">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5495" data-mp="v1rwh0vvk8tj">
                      {t('Bloque ▾', 'Blocks ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5496" data-mp="14vh17r8kqj5r">
                      R-72 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5497" data-mp="c58poi8jmx05">
                      {t('Nid-de-poule rue Principale', 'Pothole on Principale Street')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('Reçue')}`} data-id="element-5498" data-mp="m47rphyszue6">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('Reçue')}`} data-id="element-5499" data-mp="1xh0ioqjrset6"></div>
                    {tr('Reçue')}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 hover:bg-slate-50 rounded-lg border border-transparent hover:border-slate-100 transition-colors" data-id="element-5500" data-mp="3d1j6tg9bq2mg">
                  <div className="flex items-center gap-3" data-id="element-5501" data-mp="33bzn68dse4qj">
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5502" data-mp="339dfm8qwbp8b">
                      {t('Doublon ▾', 'Duplicate ▾')}
                    </span>
                    <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-1 rounded border border-slate-200" data-id="element-5503" data-mp="2pbf73jz9an0q">
                      R-75 ↗
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-5504" data-mp="2keop2r3w5hco">
                      {t('Banc de parc endommagé', 'Damaged park bench')}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1.5 ${getStatusColor('En Cours')}`} data-id="element-5505" data-mp="iiw7irk2ihsi">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot('En Cours')}`} data-id="element-5506" data-mp="3ge0l67dbkxf7"></div>
                    {tr('En Cours')}
                  </div>
                </div>
              </div>
              <button className="text-sm text-slate-500 font-medium flex items-center gap-1 hover:text-slate-700 mt-3" data-id="element-5507" data-mp="2sozjd2yzskiw">
                <PlusIcon className="w-4 h-4" data-id="element-5508" data-mp="2xg7vi6rpijzr" /> {t('Lier une requête', 'Link a request')}
              </button>
            </div>

            {/* Tags */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5509" data-mp="2jm6raxhzk4s0">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5510" data-mp="2onw4ojsokuj9">
                {t('ÉTIQUETTES', 'TAGS')}
              </h3>
              <div className="flex flex-wrap gap-2" data-id="element-5511" data-mp="2eox4zqpw898e">
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-5512" data-mp="2eof0y7wzoqpx">
                  {t('Urgent', 'Urgent')} <XIcon className="w-3 h-3 cursor-pointer" data-id="element-5513" data-mp="1r34n6n4atkok" />
                </span>
                <span className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-xs font-medium border border-indigo-100 flex items-center gap-1" data-id="element-5514" data-mp="251uibx7k5aaa">
                  {t('Suivi requis', 'Follow-up needed')} <XIcon className="w-3 h-3 cursor-pointer" data-id="element-5515" data-mp="2e1hb5um817rq" />
                </span>
                <button className="px-3 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 text-xs font-medium flex items-center gap-1 hover:border-slate-400 hover:text-slate-700 transition-colors" data-id="element-5516" data-mp="2a177iolwitg1">
                  <PlusIcon className="w-3 h-3" data-id="element-5517" data-mp="2iu0ocqgzy0w0" /> {t('Ajouter une étiquette', 'Add a tag')}
                </button>
              </div>
            </div>

            {/* Internal Files */}
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-id="element-5518" data-mp="2srybtbr17tnk">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-5519" data-mp="2zun1veyngfta">
                {t('FICHIERS INTERNES', 'INTERNAL FILES')}
              </h3>
              <div className="flex gap-3" data-id="element-5520" data-mp="34rdiq4ia3ffr">
                <div className="w-20 rounded-lg border border-slate-200 p-2 flex flex-col items-center justify-center text-center" data-id="element-5521" data-mp="39s19qq6ibw9z">
                  <FileTextIcon className="w-8 h-8 text-red-500 mb-2" data-id="element-5522" data-mp="3ekubd56oiohu" />
                  <div className="text-[10px] font-medium text-slate-700 truncate w-full" data-id="element-5523" data-mp="3jj465eohrn2x">
                    rapport_inspe...
                  </div>
                  <div className="text-[9px] text-slate-400" data-id="element-5524" data-mp="3nm0rhd5f1xak">{t('22 mai', 'May 22')}</div>
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5525" data-mp="1cpu5l9s9n76">
                  <img src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-5526" data-mp="1an4518mru2g" />
                </div>
                <div className="w-20 h-24 rounded-lg bg-slate-200 overflow-hidden" data-id="element-5527" data-mp="1vlmv9eqgwfuq">
                  <img src="https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=200&h=200&fit=crop" alt="Doc" className="w-full h-full object-cover" data-id="element-5528" data-mp="1qtlcd9ysnqvq" />
                </div>
                <button className="w-20 h-24 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:text-[#4F46E5] hover:border-[#4F46E5] transition-colors" data-id="element-5529" data-mp="1gtuj1blyzhx8">
                  <PlusIcon className="w-5 h-5 mb-1" data-id="element-5530" data-mp="24kzyhpo00870" />
                  <span className="text-xs font-medium" data-id="element-5531" data-mp="24jf3jyxrun8m">{t('Téléverser', 'Upload')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-5532" data-mp="t406kv5kbvdi">
      {/* Header */}
      <div className="p-4 border-b border-slate-200" data-id="element-5533" data-mp="o9crfmwg8dbl">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4" data-id="element-5534" data-mp="1dudwj5aj41q9">
          <div className="flex items-center gap-3" data-id="element-5535" data-mp="1iv9vr5jxqzpc">
            <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 gap-1" data-id="element-5536" data-mp="14uh1mlxjq6mr">
              <button onClick={() => setViewMode('list')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5537" data-mp="14wts5nhddpbf">
                <TableIcon className="w-4 h-4" data-id="element-5538" data-mp="1ntbhjy8qxd4i" />
                {t('Tableau', 'Table')}
              </button>
              <button onClick={() => setViewMode('map')} className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${viewMode === 'map' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-5539" data-mp="1ruopcdvefqe2">
                <MapIcon className="w-4 h-4" data-id="element-5540" data-mp="8vqpvkececjz" />
                {t('Carte', 'Map')}
              </button>
            </div>
            <span className="text-sm text-slate-500" data-id="element-5541" data-mp="3w4fdipwfaev">{t('10 demandes', '10 requests')}</span>
          </div>

          <div className="flex items-center gap-3" data-id="element-5542" data-mp="ikqt5fzvnu3z">
            <div className="relative" data-id="element-5543" data-mp="dmz3ytqmam3h">
              <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-5544" data-mp="doa3uigxz9pb" />
              <input type="text" placeholder={t('Rechercher des requêtes...', 'Search requests...')} className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent" data-id="element-5545" data-mp="3m3k3ljxd02tx" />
            </div>
            <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:bg-slate-50 transition-colors" data-id="element-5546" data-mp="3r482ssvqrpz8">
              <FilterIcon className="w-4 h-4" data-id="element-5547" data-mp="16bd1l8dsngei" />
              {t('Filtrer', 'Filter')}
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 transition-colors whitespace-nowrap" data-id="element-5548" data-mp="1kw4jhjxmw8cy">
              <PlusIcon className="w-4 h-4" data-id="element-5549" data-mp="16zhmjh5oli71" />
              {t('Créer une requête', 'Create a request')}
            </button>
          </div>
        </div>
      </div>

      {/* Map View */}
      {viewMode === 'map' && <div className="h-[400px] w-full border-b border-slate-200" data-id="element-5550" data-mp="1yq5jsi48i3ff">
          <MapContainer center={[45.4499, -73.4653]} zoom={13} scrollWheelZoom={false} style={{
        height: '100%',
        width: '100%'
      }} data-id="element-5551" data-mp="1ty3zqa0tr8eu">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-5552" data-mp="2mgrk1n7zyjev" />
            {MAP_MARKERS.map(m => <Marker key={m.reqId} position={m.position} icon={requestMarkerIcon} data-id="element-5553" data-mp="94ey8veacesp">
                <Popup data-id="element-5554" data-mp="d4yy46q8twij">
                  <div className="text-sm" data-id="element-5555" data-mp="d8v1eowil848">
                    <div className="font-semibold text-slate-900" data-id="element-5556" data-mp="i6o7lhxti24i">
                      {m.reqId} — {tr(m.subject)}
                    </div>
                    <div className="text-slate-500" data-id="element-5557" data-mp="n2lnjn5656gr">{tr(m.category)}</div>
                  </div>
                </Popup>
              </Marker>)}
          </MapContainer>
        </div>}

      {/* Table */}
      <div className="overflow-x-auto" data-id="element-5558" data-mp="10w35j3i7ib4o">
        <table className="w-full text-left border-collapse" data-id="element-5559" data-mp="10v1mocuy8kce">
          <thead data-id="element-5560" data-mp="15vft5qgwjm8b">
            <tr className="border-b border-slate-200 bg-white" data-id="element-5561" data-mp="15vz1cfgr2w95">
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5562" data-mp="15tcwwop5owv5">
                <span className="inline-flex items-center gap-1" data-id="element-5563" data-mp="1arxsubxdkuds">
                  {t('Sujet', 'Subject')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5564" data-mp="31ylxp1sxp4cu" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5565" data-mp="3gni3stbfk7at">
                <span className="inline-flex items-center gap-1" data-id="element-5566" data-mp="3bw05kd0skktj">
                  {t('Catégorie', 'Category')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5567" data-mp="3pmvbbgfzulsw" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5568" data-mp="3lls2vf09zs1p">
                <span className="inline-flex items-center gap-1" data-id="element-5569" data-mp="3g6n3smwgz9u">
                  {t('ID Requête', 'Request ID')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5570" data-mp="3ul6oo15hzefv" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5571" data-mp="2098enwzd6eix">
                {t('Emplacement', 'Location')}
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5572" data-mp="frprvhfaiq8k">
                <span className="inline-flex items-center gap-1" data-id="element-5573" data-mp="kp96iifwh511">
                  {t('Date de création', 'Created on')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5574" data-mp="b07rovl3cton" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5575" data-mp="3t5b0ore6hpuc">
                <span className="inline-flex items-center gap-1" data-id="element-5576" data-mp="1z9i7xrvvtyo">
                  {t('Statut', 'Status')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5577" data-mp="3j8g3qcxjzxi6" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5578" data-mp="3o7ix30468fab">
                <span className="inline-flex items-center gap-1" data-id="element-5579" data-mp="1hhl4tc81vi78">
                  {t('Département', 'Department')}{' '}
                  <ChevronsUpDownIcon className="w-3.5 h-3.5 text-slate-400" data-id="element-5580" data-mp="1melahm0zmixt" />
                </span>
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5581" data-mp="3ig6jp5ivse3f">
                {t('Demandeur', 'Requester')}
              </th>
              <th className="px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap" data-id="element-5582" data-mp="39gjfyd2oiw77">
                {t('Date SLA', 'SLA date')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-5583" data-mp="2zkghbh71dfp0">
            {MOCK_REQUESTS.map(req => <tr key={req.id} onClick={() => setSelectedRequest(req)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-id="element-5584" data-mp="2us563i6ui7h5">
                <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap" data-id="element-5585" data-mp="2ltjpvtouj4jj">
                  <div className="flex items-center gap-2" data-id="element-5586" data-mp="2gtfbc97i0jvy">
                    <div className="w-8 h-8 rounded bg-slate-50 border border-slate-200 flex items-center justify-center" data-id="element-5587" data-mp="2bu2mjix2h2qq">
                      <FolderIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-5588" data-mp="21qnln53q4s9x" />
                    </div>
                    {tr(req.subject)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5589" data-mp="2bobraq2vbk6v">
                  {tr(req.category)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5590" data-mp="2gmdarc47hliv">
                  {req.reqId}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5591" data-mp="1ixjnp7jh3t9i">
                  <div className="flex items-center gap-1" data-id="element-5592" data-mp="1nz93f0hn4v3x">
                    {req.location}
                    <MapPinIcon className="w-3 h-3 text-[#4F46E5]" data-id="element-5593" data-mp="1sw98p96o1ttz" />
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5594" data-mp="1xpvcscyrkpe6">
                  {tr(req.date)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap" data-id="element-5595" data-mp="157zr6jghjs8n">
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(req.status)}`} data-id="element-5596" data-mp="15653ixdtxm0v">
                    <div className={`w-1.5 h-1.5 rounded-full ${getStatusDot(req.status)}`} data-id="element-5597" data-mp="s7xjro1dvr9n"></div>
                    {tr(req.status)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5598" data-mp="120d0yj4uwbth">
                  {tr(req.department)}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5599" data-mp="9aeb4liu8anr">
                  <div className="flex items-center gap-2" data-id="element-5600" data-mp="o7eqrqnr8f5v">
                    <div className={`w-6 h-6 rounded-full ${req.requester.color} text-white flex items-center justify-center text-[10px] font-bold`} data-id="element-5601" data-mp="o4sj7r0v5znn">
                      {req.requester.initials}
                    </div>
                    {req.requester.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 whitespace-nowrap" data-id="element-5602" data-mp="22672vg5kqsno">
                  {tr(req.slaDate)}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-6 text-sm text-slate-500" data-id="element-5603" data-mp="1t7lc4xpy87bt">
        <div className="flex items-center gap-2" data-id="element-5604" data-mp="1x96xz8kgnvy5">
          {t('Éléments par page', 'Items per page')}
          <select className="border-b border-slate-300 pb-1 focus:outline-none bg-transparent font-medium text-slate-700" data-id="element-5605" data-mp="2kzaqokw42bad">
            <option data-id="element-5606" data-mp="2c563cbg6rhzl">10</option>
            <option data-id="element-5607" data-mp="2c3uye8b0846w">20</option>
            <option data-id="element-5608" data-mp="2c2jv09lh0ygb">50</option>
          </select>
        </div>
        <div data-id="element-5609" data-mp="2g56jc0ixo18r">1 - 10 / 98</div>
        <div className="flex items-center gap-4" data-id="element-5610" data-mp="2g4et7583uvpv">
          <button className="hover:text-slate-900" aria-label={t('Page précédente', 'Previous page')} data-id="element-5611" data-mp="33vkalotp1w2a">
            <ChevronLeftIcon className="w-4 h-4" data-id="element-5612" data-mp="38msiggfdl9dc" />
          </button>
          <button className="hover:text-slate-900" aria-label={t('Page suivante', 'Next page')} data-id="element-5613" data-mp="2jvwebf1fvvgv">
            <ChevronRightIcon className="w-4 h-4" data-id="element-5614" data-mp="2f56aw7ckq2ot" />
          </button>
        </div>
      </div>

      {showCreate && <CreateRequeteDialog onClose={() => setShowCreate(false)} data-id="element-5615" data-mp="2a5jwhul0egef" />}
    </div>;
}
// Missing icons for the component
function CalendarIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5616" data-mp="32vaa82iqbga0">
      <rect width="18" height="18" x="3" y="4" rx="2" ry="2" data-id="element-5617" data-mp="2synrpkdvl1b5" />
      <line x1="16" x2="16" y1="2" y2="6" data-id="element-5618" data-mp="2nwonk8aba0d5" />
      <line x1="8" x2="8" y1="2" y2="6" data-id="element-5619" data-mp="2nzubhk77ew7a" />
      <line x1="3" x2="21" y1="10" y2="10" data-id="element-5620" data-mp="1hhnymmjpeig2" />
    </svg>;
}
function UserIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5621" data-mp="1cgrpnukhw63s">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" data-id="element-5622" data-mp="1xybsiacarxj" />
      <circle cx="12" cy="7" r="4" data-id="element-5623" data-mp="1wn4w9lll427" />
    </svg>;
}
function PencilIcon(props: React.SVGProps<SVGSVGElement>) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5624" data-mp="bojeohx1qim9">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" data-id="element-5625" data-mp="kpqzpyajxmye" />
      <path d="m15 5 4 4" data-id="element-5626" data-mp="pmzkm6n3453c" />
    </svg>;
}